import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';

type Props = {
  step: 1 | 2;
};

const STEPS: ReadonlyArray<{ id: 1 | 2; label: string }> = [
  { id: 1, label: 'Create account' },
  { id: 2, label: 'Start free trial' },
];

const targetPctForStep = (step: 1 | 2): number => (step === 1 ? 6 : 100);

export const RegistrationProgress: React.FC<Props> = ({ step }) => {
  const [fillPct, setFillPct] = useState<number>(step === 1 ? 0 : 6);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      setFillPct(targetPctForStep(step));
    });
    return () => window.cancelAnimationFrame(id);
  }, [step]);

  return (
    <div className="mx-auto w-full max-w-md pt-1">
      <div className="relative">
        <div
          className="absolute left-16 right-16 top-4 h-[3px] -translate-y-1/2 rounded-full bg-outline-variant"
          aria-hidden="true"
        />
        <div
          className="absolute left-16 top-4 h-[3px] -translate-y-1/2 rounded-full bg-primary transition-[width] duration-700 ease-out motion-reduce:transition-none"
          style={{ width: `calc((100% - 8rem) * ${fillPct} / 100)` }}
          aria-hidden="true"
        />

        <ol className="relative flex items-start justify-between">
          {STEPS.map((s) => {
            const completed = s.id < step;
            const active = s.id === step;
            const nodeClasses = [
              'flex h-8 w-8 items-center justify-center rounded-full border-2 transition-[background-color,border-color,color,transform] duration-500 ease-out',
              completed
                ? 'border-primary bg-primary text-on-primary'
                : active
                ? 'scale-110 border-accent bg-surface text-accent shadow-[0_0_0_4px_rgba(91,63,208,0.12)]'
                : 'border-outline-variant bg-surface text-on-surface-variant',
            ].join(' ');
            return (
              <li key={s.id} className="flex w-32 flex-col items-center gap-2">
                <div className={nodeClasses} aria-current={active ? 'step' : undefined}>
                  {completed ? (
                    <Check size={16} strokeWidth={2.8} />
                  ) : (
                    <span className="text-sm font-semibold">{s.id}</span>
                  )}
                </div>
                <span
                  className={[
                    'text-center text-xs font-semibold',
                    active || completed ? 'text-on-surface' : 'text-on-surface-variant',
                  ].join(' ')}
                >
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
};
